import * as React from "react"

import { cn } from "@/lib/utils"
import { CARD, CARD_DESCRIPTION, CARD_HEADER, CARD_TITLE } from "@/shadcn/components/styles"
import { Badge } from "@/shadcn/components/ui/badge"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card"

import { bucketStates, bucketTitles, disclosure, passingRollUp, sectionTally, skippedReason } from "./copy"
import AccessibilityDetails from "./details/AccessibilityDetails"
import ContentDetails from "./details/ContentDetails"
import SettingsDetails from "./details/SettingsDetails"
import SocialDetails from "./details/SocialDetails"
import Disclosure from "./Disclosure"
import MedicCheckList from "./MedicCheckList"
import { BUCKET_TONE, bucketDisplayState, partitionFindings } from "./severity"
import type { BucketByName, BucketName } from "./types"

/** Label for each section's raw-detail disclosure, naming what's inside it. */
const DETAIL_LABEL: Record<BucketName, string> = {
  accessibility: disclosure.feedResponse,
  content: disclosure.sampledItems,
  social: disclosure.perDestination,
  settings: disclosure.filterDefinition,
}

/**
 * Each detail renderer reads one bucket-specific shape. When that shape is
 * absent the twist-out would open onto nothing, so the trigger is not rendered
 * at all rather than offering an empty panel.
 */
function hasRawDetails<N extends BucketName>(name: N, bucket: BucketByName[N]): boolean {
  switch (name) {
    case "accessibility": {
      const b = bucket as BucketByName["accessibility"]
      return Boolean(b.ourFetch ?? b.caching)
    }
    case "content": {
      const b = bucket as BucketByName["content"]
      return (b.items?.length ?? 0) > 0
    }
    case "social": {
      const b = bucket as BucketByName["social"]
      return (b.perDestination?.length ?? 0) > 0
    }
    case "settings": {
      const b = bucket as BucketByName["settings"]
      return Boolean(b.filterReport)
    }
    default:
      return false
  }
}

/* Narrowing by name in a switch keeps each renderer's bucket type exact. */
function SectionDetails<N extends BucketName>({ name, bucket }: { name: N; bucket: BucketByName[N] }) {
  switch (name) {
    case "accessibility":
      return <AccessibilityDetails bucket={bucket as BucketByName["accessibility"]} />
    case "content":
      return <ContentDetails bucket={bucket as BucketByName["content"]} />
    case "social":
      return <SocialDetails bucket={bucket as BucketByName["social"]} />
    case "settings":
      return <SettingsDetails bucket={bucket as BucketByName["settings"]} />
    default:
      return null
  }
}

/**
 * Sections that ran but produced nothing to act on, and sections that never ran
 * at all, collapse to a single line. As full cards they each occupied about
 * seventy vertical pixels to communicate one word.
 */
function QuietSection({ title, state, note }: { title: string; state: "pending" | "running" | "skipped"; note?: string }) {
  const tone = BUCKET_TONE[state]
  const StateIcon = tone.icon

  return (
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:px-4 tw:py-2.5">
      <div className="tw:flex tw:min-w-0 tw:flex-wrap tw:items-baseline tw:gap-x-2">
        <span className="tw:text-sm tw:font-medium tw:text-muted-foreground">{title}</span>
        {note && <span className="tw:text-xs tw:text-muted-foreground">{note}</span>}
      </div>
      <Badge variant="outline" className={cn("tw:shrink-0", tone.badge)}>
        {StateIcon && (
          <StateIcon
            aria-hidden="true"
            className={cn("tw:size-3", state === "running" && "tw:animate-spin")}
          />
        )}
        {bucketStates[state]}
      </Badge>
    </div>
  )
}

export default function MedicSection<N extends BucketName>({
  name,
  bucket,
  runTerminal,
}: {
  name: N
  bucket?: BucketByName[N]
  runTerminal: boolean
}) {
  if (!bucket) return null

  const title = bucketTitles[name]
  const state = bucketDisplayState(bucket, runTerminal)

  if (state === "pending" || state === "running" || state === "skipped") {
    return (
      <QuietSection
        title={title}
        state={state}
        note={bucket.status === "skipped" && bucket.reason ? skippedReason(bucket.reason) : undefined}
      />
    )
  }

  const tone = BUCKET_TONE[state]
  const { problems, scopeNotes, passing } = partitionFindings(bucket.findings)
  const passed = passing.filter((f) => f.severity !== "skipped").length
  const skipped = passing.length - passed
  const rawDetails = hasRawDetails(name, bucket)
  /*
   * `error`-status buckets carry the same sentence in both `summary` and
   * `error`, so printing both renders the identical line twice in two colours.
   */
  const errorText = bucket.error && bucket.error !== bucket.summary ? bucket.error : undefined

  const hasBody = problems.length > 0 || scopeNotes.length > 0 || passing.length > 0 || rawDetails

  const StateIcon = tone.icon
  const counts = {
    issues: problems.filter((f) => f.severity === "critical").length,
    warnings: problems.filter((f) => f.severity === "warning").length,
    passed,
  }

  /*
   * The severity colour lives in a tinted header band rather than on each
   * finding row. It reads at a glance while scrolling, and states it once per
   * section instead of once per finding.
   *
   * The band is the header's own background, clipped by CARD's overflow-hidden,
   * so it cannot disagree with the corner radius — the earlier `border-t`
   * approach had to curve the colour through the corner arc, which made it read
   * as a pill sitting on the card rather than part of it.
   */
  return (
    <Card className={CARD}>
      {/* CARD_HEADER's p-4 is sized for a white header carrying a title and a
          description; here the band holds a single line, so the vertical padding
          is tightened to keep it a slim rule rather than a block. Horizontal
          padding stays on the house value so the title lines up with the body. */}
      <CardHeader className={cn(CARD_HEADER, "tw:py-2 tw:gap-0.5 tw:rounded-none", tone.band)}>
        <CardTitle className={cn(CARD_TITLE, "tw:flex tw:items-center tw:gap-2 tw:text-inherit")}>
          {/* pending/running/skipped return early above, so no spin state here.
              The icon takes its own tone rather than inheriting the title's, so
              the glyph sits a step lighter than the words. */}
          <StateIcon aria-hidden="true" className={cn("tw:size-4 tw:shrink-0", tone.iconTone)} />
          {title}
        </CardTitle>
        <CardAction>
          {/* The band's colour already says which state this is, so the slot
              carries the count instead of repeating the word. */}
          <span className="tw:text-sm tw:font-medium tw:tabular-nums">
            {sectionTally(state, counts)}
          </span>
        </CardAction>
      </CardHeader>

      {(bucket.summary || errorText || hasBody) && (
        <CardContent className="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:py-3.5">
          {bucket.summary && (
            <CardDescription className={CARD_DESCRIPTION}>{bucket.summary}</CardDescription>
          )}
          {errorText && (
            <CardDescription className={cn(CARD_DESCRIPTION, "tw:font-medium tw:text-destructive")}>
              {errorText}
            </CardDescription>
          )}
          {/*
           * Problems are not behind a disclosure. They are the reason the reader
           * opened the report, and previously sat one click down, weighted the
           * same as the dozen rows confirming that nothing was wrong.
           */}
          {problems.length > 0 && <MedicCheckList findings={problems} />}
          {/* Stays out of the roll-up: it narrows what the rows around it cover. */}
          {scopeNotes.length > 0 && <MedicCheckList findings={scopeNotes} />}
          {passing.length > 0 && (
            <Disclosure label={passingRollUp(passed, skipped)}>
              <MedicCheckList findings={passing} />
            </Disclosure>
          )}
          {rawDetails && (
            <Disclosure label={DETAIL_LABEL[name]}>
              <div className="tw:rounded-md tw:bg-muted/30 tw:p-3">
                <SectionDetails name={name} bucket={bucket} />
              </div>
            </Disclosure>
          )}
        </CardContent>
      )}
    </Card>
  )
}
